import React from 'react';
import { Grid, Hidden, Typography } from '@mui/material';
import { getImagePathSafely } from 'app/util/images';

/**
 * Hides the hero on small screens unless the page opts in to showing it on mobile.
 *
 * Declared at module scope so its component identity is stable across renders. When it was declared inside
 * `HeroImage`, every re-render (e.g. a redux-form blur) created a new component type, so React unmounted and
 * remounted the whole hero. The page briefly lost the hero's height, the browser clamped the scroll position,
 * and a button under the cursor moved before mouse-up, swallowing the click.
 *
 * @param {object} props - Component props.
 * @param {React.ReactNode} props.children - The hero content to wrap.
 * @param {boolean} [props.showMobileImage] - When true, the hero is shown on every screen size.
 * @returns {JSX.Element} The children, hidden below the `md` breakpoint unless `showMobileImage` is set.
 */
const HeroImageWrapper = ({ children, showMobileImage }: { children: React.ReactNode; showMobileImage?: boolean }) => {
  return !showMobileImage ? <Hidden mdDown>{children}</Hidden> : <React.Fragment>{children}</React.Fragment>;
};

export const HeroImage = ({ ...props }: any) => {
  const {
    contentContainerClass,
    heroContainerClass,
    heroGridProps,
    heroImageClass,
    heroTitleClass,
    image,
    pageTitle,
    removeFormContainer,
    showMobileImage,
    staticImage,
    titleClass,
  } = props;
  const imagePath = staticImage ? image : getImagePathSafely(image);
  // Longhand properties only: with the `background` shorthand plus `backgroundSize`, React re-applies the shorthand
  // when the image changes on a re-render, which resets background-size to auto and leaves the image uncovered.
  const heroImage = {
    backgroundColor    : 'black',
    backgroundImage    : `url('${imagePath}')`,
    backgroundPosition : '50%',
    backgroundRepeat   : 'no-repeat',
    backgroundSize     : 'cover',
    position           : 'relative',
    width              : '100%',
  } as React.CSSProperties;

  // If no custom properties are set, the default styling will be used.
  const isContentContainer = removeFormContainer ? contentContainerClass : 'formWrapper';
  const isHeroContainer    = removeFormContainer ? heroContainerClass : 'formWrapper';
  const isHeroImageClass   = heroImageClass ? heroImageClass : 'heroImage';

  return (
    <React.Fragment>
      <HeroImageWrapper showMobileImage={showMobileImage}>
        <Grid
          alignItems = "center"
          classes    = {{ container: `${isHeroImageClass}` }}
          container
          justifyContent = "center"
          style   = {heroImage}
          {...heroGridProps}
        >
          <Grid item classes={{ item: 'heroContent' }}>
            <Grid container justifyContent="center" classes={{ container: `${isHeroContainer}` }}>
              <Grid item>
                <Typography
                  variant = "h2"
                  classes = {{
                    root    : `${heroTitleClass}`,
                    h2: 'heroTitle',
                  }}
                >
                  {pageTitle}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </HeroImageWrapper>
      {!showMobileImage && (
        <Hidden mdUp>
          <Grid container classes={{ container: `${isContentContainer}` }}>
            <Grid item xs={12}>
              <Typography
                variant   = "h6"
                component = "h1"
                classes   = {{
                  root : `${titleClass}`,
                  h6: 'titleMargin',
                }}
              >
                {pageTitle}
              </Typography>
            </Grid>
          </Grid>
        </Hidden>
      )}
      <Grid container classes={{ container: `${isContentContainer}` }}>
        <Grid item xs={12}>
          {props.children}
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
